import { BridgeComponent } from "@hotwired/hotwire-native-bridge"

// Tells the native shell whether a form opened as a modal has unsaved changes,
// so closing the modal asks before discarding them (CONEK-8214). iOS asks on
// its ✕ and on a swipe-down of the sheet; Android on the system back.
//
// Mounted on a wrapper around the form, not on the <form>: the task form's
// whole-form reload can swap the <form> element, and a fresh snapshot then
// would read the rep's own edits as the starting state.
//
// Dirty = the form's fields differ from the snapshot taken on connect. Values
// filled from code (Title autofill, dependent selects) count once any event
// of the rep's has run, which is when this compares. A form re-rendered after
// a failed submit carries `start-dirty`: what it holds is the rep's unsaved
// input, not a saved state.
//
// A submit marks the form clean, since a successful Save leaves the modal
// through the server's redirect. Two ways it doesn't (CONEK-8230):
// - the request fails on the network: Turbo ends the submission with
//   `success: false` and nothing is re-rendered, so the form is checked again;
// - Android's offline queue cancels the submit and queues the write natively
//   (`bridge--offline-queue:queued`): the data is safe, so the form is clean
//   and what it holds now is the new starting state.
//
// The dialog's copy comes from Rails i18n in the payload, so neither app
// needs translations of its own.
//
// Wire contract (docs/technical/turbo-native-bridge.md, `bridge--form-guard`):
//   web → native
//     "connect"  { dirty, title, message, discard, keep }
//     "change"   { dirty }  — only when the state flips
//   No reply.
const IGNORED_FIELDS = new Set(["authenticity_token", "_method"])
const QUEUED_EVENT = "bridge--offline-queue:queued"

export default class extends BridgeComponent {
  static component = "form-guard"
  static values = {
    startDirty: Boolean,
    title: String,
    message: String,
    discard: String,
    keep: String,
  }

  connect() {
    super.connect()
    this.snapshot = this.serialize()
    this.dirty = this.startDirtyValue
    this.onEdit = this.onEdit.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onSubmitEnd = this.onSubmitEnd.bind(this)
    this.onQueued = this.onQueued.bind(this)
    for (const type of ["input", "change", "lexxy:change"])
      this.element.addEventListener(type, this.onEdit)
    this.element.addEventListener("submit", this.onSubmit)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    this.element.addEventListener(QUEUED_EVENT, this.onQueued)

    this.send("connect", {
      dirty: this.dirty,
      title: this.titleValue,
      message: this.messageValue,
      discard: this.discardValue,
      keep: this.keepValue,
    })
  }

  disconnect() {
    for (const type of ["input", "change", "lexxy:change"])
      this.element.removeEventListener(type, this.onEdit)
    this.element.removeEventListener("submit", this.onSubmit)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    this.element.removeEventListener(QUEUED_EVENT, this.onQueued)
    super.disconnect()
  }

  onEdit() {
    // After the event has run its course: dependent fields (Title autofill,
    // Tom Select's hidden <select>) are written by listeners of this same event.
    setTimeout(() => {
      if (!this.element.isConnected) return
      this.recheck()
    }, 0)
  }

  // Turbo ends every submission it handles with `turbo:submit-end`. When it
  // did not succeed and the form is still here (no redirect, no re-render),
  // whatever the rep typed is still unsaved. A 422 also ends with
  // `success: false`; its re-render reconnects with `start-dirty`.
  onSubmitEnd(event) {
    if (!this.isGuardedForm(event.target)) return
    if (event.detail?.success !== false || !this.element.isConnected) return
    this.recheck()
  }

  // Dispatched by bridge--offline-queue on the <form> once the native queue
  // holds the write (offline_queue_controller.js#handleEnqueueReply).
  onQueued(event) {
    if (!this.isGuardedForm(event.target) || !this.element.isConnected) return
    this.snapshot = this.serialize()
    this.startDirtyValue = false
    this.update(false)
  }

  recheck() {
    this.update(this.startDirtyValue || this.serialize() !== this.snapshot)
  }

  // A submit leaves the modal through the server's redirect, not through the
  // rep closing it: nothing to guard. A blocked submit (empty required field)
  // fires no `submit`, so the form stays guarded.
  onSubmit(event) {
    if (!this.isGuardedForm(event.target) || event.defaultPrevented) return
    this.update(false)
  }

  // Other forms can sit inside the wrapper: the task edit screen renders a
  // `button_to` <form> per existing attachment next to (not inside) the task
  // form. Their submits say nothing about the task form's unsaved changes.
  isGuardedForm(target) {
    return target === this.guardedForm()
  }

  guardedForm() {
    return this.element instanceof HTMLFormElement
      ? this.element
      : this.element.querySelector("form")
  }

  update(dirty) {
    if (dirty === this.dirty) return
    this.dirty = dirty
    this.send("change", { dirty })
  }

  serialize() {
    const form = this.guardedForm()
    if (!form) return ""
    const entries = []
    for (const [name, value] of new FormData(form)) {
      if (IGNORED_FIELDS.has(name)) continue
      entries.push([
        name,
        value instanceof File ? `${value.name}:${value.size}` : value,
      ])
    }
    return JSON.stringify(entries)
  }
}
